import { z } from 'zod';

import { InboxSurfaceSchema } from './inbox-surface';

// A ChannelBinding is the routing key: it composes a ChannelConnection with one
// engagement strategy and the pinned agent version that runs it. Inbound routing
// resolves (connection, event type → strategy) → binding, so the pair must be unique.

// The engagement strategy, as an enum (no table). Behavior is code-keyed per strategy;
// this is the routing identity. Tuples exported so the Drizzle column enum reuses them
// and can never drift.
export const ACTIVE_STRATEGY_KEYS = [
  'reply-to-dm',
  'fan-out-on-own-media-comment',
  'reply-to-mention',
  'draft-on-inbound',
  'draft-cs-reply',
  'run-on-request',
  'reply-when-addressed',
  'reply-to-every-message',
  'append-to-every-message',
  'reply-to-shortcut',
] as const;

// Strategies whose runner is gone: nothing in the gateway dispatches these, so a binding
// naming one routes to nothing. Their historical channel_bindings rows survive — agent_run_jobs
// references them ON DELETE RESTRICT — so the key stays valid for reads and for disabling the
// dead row. The admin picker derives from ACTIVE_STRATEGY_KEYS and never offers these.
// 'investigate-on-flag' was the SBC S2S relay, retired when Intercom drafting moved onto the
// platform (ENG-1822).
export const RETIRED_STRATEGY_KEYS = ['investigate-on-flag'] as const;

export const STRATEGY_KEYS = [...ACTIVE_STRATEGY_KEYS, ...RETIRED_STRATEGY_KEYS] as const;
export const StrategyKeySchema = z.enum(STRATEGY_KEYS);
export type StrategyKey = z.infer<typeof StrategyKeySchema>;

export const ChannelBindingSchema = z.object({
  id: z.uuid(),
  channelConnectionId: z.uuid(),
  strategyKey: StrategyKeySchema,
  agentId: z.uuid(), // cross-context ref into agents (resolved, never embedded)
  enabled: z.boolean(), // disabled bindings are ignored by routing
  surface: InboxSurfaceSchema.nullable(),
  // The external sub-scope the binding applies to — for Slack, the one channel id an
  // every-message strategy watches. Null means everywhere the connection is reached.
  externalScope: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ChannelBinding = z.infer<typeof ChannelBindingSchema>;

// --- Read model for the admin UI ---

export const ChannelBindingSummarySchema = ChannelBindingSchema.extend({
  agentName: z.string(),
  agentSlug: z.string(),
  versionNumber: z.number().int().positive(),
});
export type ChannelBindingSummary = z.infer<typeof ChannelBindingSummarySchema>;

// --- Write contracts ---

export const ChannelBindingWriteSchema = z.object({
  channelConnectionId: z.uuid(),
  strategyKey: StrategyKeySchema,
  agentId: z.uuid(),
  enabled: z.boolean().default(true),
  surface: InboxSurfaceSchema.nullable().default(null),
  externalScope: z.string().trim().min(1).max(120).nullable().default(null),
});
export type ChannelBindingWrite = z.infer<typeof ChannelBindingWriteSchema>;

export const CreateChannelBindingSchema = ChannelBindingWriteSchema;
export type CreateChannelBinding = z.infer<typeof CreateChannelBindingSchema>;

export const UpdateChannelBindingSchema = ChannelBindingWriteSchema;
export type UpdateChannelBinding = z.infer<typeof UpdateChannelBindingSchema>;
